import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import mqtt from 'mqtt';
import { v4 as uuidv4 } from 'uuid';
import { isJsonString } from 'utils/helpers';

const CONFIG = {
  MQTT_HOST: process.env.REACT_APP_MQTT_DOMAIN,
  MQTT_USERNAME: process.env.REACT_APP_MQTT_USER,
  MQTT_PASSWORD: process.env.REACT_APP_MQTT_PASSWORD,
  MQTT_PORT: process.env.REACT_APP_MQTT_PORT,
  MQTT_PROTOCOL: process.env.REACT_APP_MQTT_PROTOCOL,
};

const CONNECT_STATUS = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECT: 'RECONNECT',
  DISCONNECT: 'DISCONNECT',
  ERROR: 'ERROR',
};

export const MqttContext = createContext({
  connected: false,
  connectStatus: CONNECT_STATUS.DISCONNECT,
  payload: null,
  isSubscribe: false,
  subscribe: () => {},
  unsubscribe: () => {},
  publish: () => {},
  disconnect: () => {},
});

export function MqttProvider({ children }) {
  const [client, setClient] = useState(null);
  const [connectStatus, setConnectStatus] = useState(CONNECT_STATUS.DISCONNECT);
  const [payload, setPayload] = useState(null);
  const [isSubscribe, setIsSubscribe] = useState(false);
  const subscribedTopicsRef = useRef(new Map());

  const subscribeClientTopic = useCallback((mqttClient, topic, qos = 2) => {
    if (!mqttClient || !topic) {
      return;
    }

    mqttClient.subscribe(topic, { qos }, (error) => {
      if (error) {
        console.log('Subscribe to topics error', error);
        return;
      }

      setIsSubscribe(true);
    });
  }, []);

  const createClient = useCallback(() => {
    try {
      const options = {
        keepalive: 60,
        clientId: 'portal_' + uuidv4(),
        connectTimeout: 4000,
        username: CONFIG.MQTT_USERNAME,
        password: CONFIG.MQTT_PASSWORD,
        reconnectPeriod: 1000,
        port: CONFIG.MQTT_PORT,
      };
      const CONNECT_URL = `${CONFIG.MQTT_PROTOCOL}://${CONFIG.MQTT_HOST}:${CONFIG.MQTT_PORT}`;
      setConnectStatus(CONNECT_STATUS.CONNECTING);
      setClient(mqtt.connect(CONNECT_URL, options));
    } catch (error) {
      console.log('createClient error', error);
    }
  }, []);

  useEffect(createClient, [createClient]);

  useEffect(() => {
    if (!client) {
      return undefined;
    }

    const handleConnect = () => {
      setConnectStatus(CONNECT_STATUS.CONNECTED);

      subscribedTopicsRef.current.forEach((storedQos, topic) => {
        subscribeClientTopic(client, topic, storedQos);
      });
    };

    const handleError = (err) => {
      console.error('Connection error: ', err);
      setConnectStatus(CONNECT_STATUS.ERROR);
    };

    const handleReconnect = () => {
      setConnectStatus(CONNECT_STATUS.RECONNECT);
    };

    const handleClose = () => {
      setConnectStatus(CONNECT_STATUS.DISCONNECT);
    };

    const handleOffline = () => {
      setConnectStatus(CONNECT_STATUS.DISCONNECT);
    };

    const handleMessage = (topic, message) => {
      setPayload({
        topic,
        message: message.toString(),
      });
    };

    client.on('connect', handleConnect);
    client.on('error', handleError);
    client.on('reconnect', handleReconnect);
    client.on('close', handleClose);
    client.on('offline', handleOffline);
    client.on('message', handleMessage);

    return () => {
      client.removeListener('connect', handleConnect);
      client.removeListener('error', handleError);
      client.removeListener('reconnect', handleReconnect);
      client.removeListener('close', handleClose);
      client.removeListener('offline', handleOffline);
      client.removeListener('message', handleMessage);
    };
  }, [client, subscribeClientTopic]);

  useEffect(() => {
    return () => {
      if (client) {
        client.end(true);
      }
    };
  }, [client]);

  const subscribe = useCallback((topic, qos = 2) => {
    if (!topic) {
      return;
    }

    subscribedTopicsRef.current.set(topic, qos);

    if (client?.connected) {
      subscribeClientTopic(client, topic, qos);
    }
  }, [client, subscribeClientTopic]);

  const unsubscribe = useCallback((topic) => {
    if (!topic) {
      return;
    }

    subscribedTopicsRef.current.delete(topic);

    client?.unsubscribe(topic, (error) => {
      if (error) {
        console.log('Unsubscribe error', error);
        return;
      }

      setIsSubscribe(subscribedTopicsRef.current.size > 0);
    });
  }, [client]);

  const publish = useCallback((topic, payload, qos = 2) => {
    client?.publish(topic, payload, { qos }, (error) => {
      if (error) {
        console.log('Publish error: ', error);
      }
    });
  }, [client]);

  const disconnect = useCallback(() => {
    client?.end(() => {
      setConnectStatus(CONNECT_STATUS.DISCONNECT);
    });
  }, [client]);

  return (
    <MqttContext.Provider
      value={{
        connected: connectStatus === CONNECT_STATUS.CONNECTED,
        connectStatus,
        payload,
        isSubscribe,
        subscribe,
        unsubscribe,
        publish,
        disconnect,
      }}>
      {children}
    </MqttContext.Provider>
  );
}

export const useMqttSubscribe = (topic, qos = 2) => {
  const { payload, subscribe, unsubscribe } = useContext(MqttContext);

  useEffect(() => {
    if (!topic) {
      return undefined;
    }

    subscribe(topic, qos);

    return () => {
      unsubscribe(topic);
    };
  }, [qos, subscribe, topic, unsubscribe]);

  return payload?.topic === topic
    ? isJsonString(payload?.message)
      ? JSON.parse(payload?.message)
      : payload?.message
    : null;
};

export const useMqttPublish = (topic, payload, qos = 2) => {
  const { publish } = useContext(MqttContext);
  publish(topic, payload, qos);
};
